import React from "react"
import styled from "styled-components"
import {Snackbar, SnackbarContent} from "@material-ui/core"
import {colors} from "@indebted/theme"

const colorMap = {
	success: colors.green,
	error: colors.red,
} as const

type Severity = keyof typeof colorMap

type NotificationState = {
	open: boolean
	severity: Severity
	message: string
}

const Ctx = React.createContext<{
	notification: {
		success: (message: string, duration?: number) => void
		error: (message: string, duration?: number) => void
	}
} | null>(null)

const useNotification = () => {
	const context = React.useContext(Ctx)
	if (!context) {
		throw new Error("useNotification must be used within a NotificationProvider")
	}
	return context
}

function NotificationProvider({children}: {children: React.ReactNode}) {
	const [state, setState] = React.useState<NotificationState>({
		open: false,
		severity: "error",
		message: "",
	})

	const notification = {
		success: (message: string) => {
			setState({...state, open: true, severity: "success", message: message})
		},
		error: (message: string) => {
			setState({...state, open: true, severity: "error", message: message})
		},
	}

	const handleClose = () => {
		setState({...state, open: false})
	}

	const origin = {
		vertical: "top",
		horizontal: "center",
	} as const

	return (
		<Ctx.Provider value={{notification}}>
			{children}
			<Snackbar anchorOrigin={origin} open={state.open} autoHideDuration={2500} onClose={handleClose}>
				<StyledSnackbarContent message={state.message} severity={state.severity} />
			</Snackbar>
		</Ctx.Provider>
	)
}

const StyledSnackbarContent = styled(SnackbarContent)<{severity: Severity}>`
	background: ${({severity}) => colorMap[severity]};
`

export {NotificationProvider, useNotification}
